/**
 * Preparation phase — both players may place a Stance card before round 1.
 *
 * Each player gets a panel showing their hand. Clicking a Stance card places
 * it into the stance slot (player.stanceCardId). Buttons:
 *   - "Confirm Stance & Ready" if a stance is placed
 *   - "Skip Stance & Ready"   if not
 * After both players are ready, parent shows the "Begin Match" CTA.
 */
import { Button } from "@/components/ui/Button";
import { BoardCard } from "./BoardCard";
import type { Card } from "@/types/card";
import type { GameState, PlayerState } from "@/types/game";
import { cn } from "@/lib/utils";

interface PrepPhaseViewProps {
  game: GameState;
  cardMap: Map<string, Card>;
  onPlaceStance: (playerNum: 1 | 2, cardDbId: string | null) => void;
  onReady: (playerNum: 1 | 2) => void;
  onBegin: () => void;
  /** Pass-and-play: render only this player's panel (the other player
   *  must not see their hand). When omitted, both panels render side-by-side. */
  singlePlayerNum?: 1 | 2;
}

function PlayerPrepPanel({
  player,
  ready,
  cardMap,
  onPlaceStance,
  onReady,
}: {
  player: PlayerState;
  ready: boolean;
  cardMap: Map<string, Card>;
  onPlaceStance: (cardDbId: string | null) => void;
  onReady: () => void;
}) {
  const handCards = player.hand
    .map((id) => cardMap.get(id))
    .filter(Boolean) as Card[];
  const stanceCardsInHand = handCards.filter((c) => c.cardType === "Stance");
  const stanceCard = player.stanceCardId ? cardMap.get(player.stanceCardId) : null;

  return (
    <div
      className={cn(
        "space-y-3 rounded-lg border bg-[#1a1d24] p-4",
        ready ? "border-amber/40 bg-amber-500/5" : "border-[#2a2f3a]",
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-[#e2e8f0]">{player.name}</span>
        {ready && (
          <span className="text-xs font-semibold text-amber-soft">✓ Ready</span>
        )}
      </div>

      {stanceCard && (
        <p className="text-xs text-teal-300">
          Stance: <span className="font-semibold">{stanceCard.name}</span>
        </p>
      )}

      <p className="text-[11px] text-[#64748b]">
        Click a Stance card to place it. No replacement draw in Preparation.
      </p>

      <div className="flex flex-wrap gap-2">
        {handCards.length === 0 && (
          <span className="text-xs text-[#64748b]/60">Empty hand</span>
        )}
        {handCards.map((c, idx) => {
          const isStance = c.cardType === "Stance";
          const isPlaced = player.stanceCardId === c.id;
          const interactive = isStance && !ready;
          return (
            <BoardCard
              key={`${c.id}_${idx}`}
              card={c}
              size="prep"
              muted={!interactive && !isPlaced}
              selected={isPlaced}
              onClick={
                interactive
                  ? () => onPlaceStance(isPlaced ? null : c.id)
                  : undefined
              }
            />
          );
        })}
        {stanceCardsInHand.length === 0 && (
          <span className="self-end text-xs text-[#64748b]/60">
            No Stance cards in hand
          </span>
        )}
      </div>

      {!ready && (
        <Button variant="secondary" size="sm" onClick={onReady}>
          {player.stanceCardId ? "Confirm Stance & Ready" : "Skip Stance & Ready"}
        </Button>
      )}
    </div>
  );
}

export function PrepPhaseView({
  game,
  cardMap,
  onPlaceStance,
  onReady,
  onBegin,
  singlePlayerNum,
}: PrepPhaseViewProps) {
  const p1 = game.players[1];
  const p2 = game.players[2];
  const p1Ready = !!p1.prepReady;
  const p2Ready = !!p2.prepReady;
  const bothReady = p1Ready && p2Ready;

  // Pass-and-play: render only the active prep player's panel so the other
  // player's hand stays hidden. The whoActsNow effect drives the handoff
  // overlay between P1 and P2 prep steps.
  if (singlePlayerNum) {
    const player = game.players[singlePlayerNum];
    const ready = !!player.prepReady;
    return (
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-xl font-bold tracking-tight text-[#e2e8f0]">
            Preparation Phase — {player.name}
          </h1>
          <p className="mt-1 text-sm text-[#94a3b8]">
            Place a Stance card if you want one in play before Round 1, then tap Ready. The device will pass to your opponent.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <PlayerPrepPanel
            player={player}
            ready={ready}
            cardMap={cardMap}
            onPlaceStance={(cid) => onPlaceStance(singlePlayerNum, cid)}
            onReady={() => onReady(singlePlayerNum)}
          />
        </div>

        {bothReady && (
          <div className="flex justify-center pt-2">
            <Button size="lg" onClick={onBegin} className="gap-2">
              ⚔ Begin Match
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-xl font-bold tracking-tight text-[#e2e8f0]">
          Preparation Phase
        </h1>
        <p className="mt-1 text-sm text-[#94a3b8]">
          Both players may place a Stance card before Round 1. No card is drawn as replacement in Preparation.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <PlayerPrepPanel
          player={p1}
          ready={p1Ready}
          cardMap={cardMap}
          onPlaceStance={(cid) => onPlaceStance(1, cid)}
          onReady={() => onReady(1)}
        />
        <PlayerPrepPanel
          player={p2}
          ready={p2Ready}
          cardMap={cardMap}
          onPlaceStance={(cid) => onPlaceStance(2, cid)}
          onReady={() => onReady(2)}
        />
      </div>

      {bothReady ? (
        <div className="flex justify-center pt-2">
          <Button size="lg" onClick={onBegin} className="gap-2">
            ⚔ Begin Match
          </Button>
        </div>
      ) : (
        <p className="text-center text-xs text-[#64748b]">
          Waiting for{" "}
          {!p1Ready && !p2Ready
            ? "both players"
            : p1Ready
              ? p2.name
              : p1.name}{" "}
          to confirm…
        </p>
      )}
    </div>
  );
}
